/* FancyLoadingMaskGLRenderer.js */

"use strict";

const TWEEN = require("tween.js");

class FancyLoadingMaskGLRenderer {
    /**
     * @member rootElement {HTMLElement} - Get the root element of the renderer.
     * @member model {String|HTMLElement} - Get the model associated with this renderer.
     */

    /**
     * Create a renderer for the veil.
     * @param model {LocalVeilGLComponent} - The model that is bound to the renderer.
     */
    constructor(model) {
        this._model = model;
        this._transitioning = false;
        this._loadingAnimationActive = false;
        this._hideAcknowledged = false;
        this._rootElem = document.createElement("fancy-loading-mask-gl");
        this._rootElem.component = this._model;

        this._containerElem = document.createElement("fancy-loading-mask-container");

        this._messageComponent = document.createElement("fancy-loading-message");
        this._messageComponent.innerText = this._model._message ? this._model._message : "";

        this._loadingMaskContainer = document.createElement("loading-mask-container");
        this._maskDots = [];
        for (let i = 1; i <= 8; i++) {
            let dot = document.createElement("span");
            dot.classList.add("position-" + i);
            this._maskDots.push(dot);
            this._loadingMaskContainer.appendChild(dot);
        }
        this._containerElem.appendChild(this._loadingMaskContainer);
        this._containerElem.appendChild(this._messageComponent);
        this._progressElem = document.createElement("fancy-progress");
        this._containerElem.appendChild(this._progressElem);
        this._rootElem.appendChild(this._containerElem);
        this._registerListeners();
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    /**
     * The shutdown promise used to kill the DOM and all members.
     * @returns {Promise}
     */
    shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._loadingAnimationActive = undefined;
            this._transitioning = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._containerElem = undefined;
            this._loadingMaskContainer = undefined;
            this._progressElem = undefined;
            this._maskDots = undefined;
            resolve();
        });
        return prom;
    }

    /**
     * Register any listeners used in the renderer.
     * @private
     */
    _registerListeners() {
        this._model.on(
            "UpdateHiddenBackgroundState",
            (this._onUpdateHiddenBackgroundState = this._onUpdateHiddenBackgroundState.bind(this))
        );
        this._model.on(
            "UpdateDarkTheme",
            (this._onUpdateDarkTheme = this._onUpdateDarkTheme.bind(this))
        );
        this._model.on("Show", (this._onShow = this._onShow.bind(this)));
        this._model.on("Hide", (this._onHide = this._onHide.bind(this)));
        this._model.on("Progress", (this._onProgress = this._onProgress.bind(this)));
        this._animationEventHandler = this._animationEventHandler.bind(this);
        this._maskDots[0].addEventListener("animationiteration", this._animationEventHandler);
        this._maskDots[4].addEventListener("animationstart", this._animationEventHandler);
        this._maskDots[4].addEventListener("animationiteration", this._animationEventHandler);
    }

    /**
     * Unregister any listeners used inside of the renderer.
     * @private
     */
    _unregisterListeners() {
        this._model.removeListener(
            "UpdateHiddenBackgroundState",
            this._onUpdateHiddenBackgroundState
        );
        this._model.removeListener("UpdateDarkTheme", this._onUpdateDarkTheme);
        this._model.removeListener("Show", this._onShow);
        this._model.removeListener("Hide", this._onHide);
        this._model.removeListener("Progress", this._onProgress);
        this._maskDots[0].removeEventListener("animationiteration", this._animationEventHandler);
        this._maskDots[4].removeEventListener("animationstart", this._animationEventHandler);
        this._maskDots[4].removeEventListener("animationiteration", this._animationEventHandler);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise}
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._updateHiddenBackgroundState(this._model.backgroundHidden);
            this._updateDarkTheme(this._model.darkTheme);
            // Show immediately
            if (this._model.shown === true) {
                this._rootElem.style.opacity = 1;
                this._rootElem.classList.add("shown");
                this._startLoadingAnimation();
            }
            resolve();
        });
        return prom;
    }

    /**
     * Returns the root element of the renderer
     * @returns {HTMLElement}
     */
    getRootElement() {
        return this._rootElem;
    }
    get rootElement() {
        return this.getRootElement();
    }

    /**
     * Returns the model associated with this renderer.
     * @returns {LocalVeilGLComponent}
     */
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Event handler generated by the model to animate the DOM into view
     * @private
     */
    _onShow() {
        this._show();
    }

    /**
     * Event handler generated by the model to animate the DOM out of view
     * @private
     */
    _onHide() {
        this._hideAcknowledged = true;
    }

    _onProgress(progressMsg) {
        this._progressElem.innerHTML = `<p>${progressMsg}</p>`;
    }

    /**
     * Event handler generated by the model to update the hidden state of the background
     * @param newState {Boolean}
     * @private
     */
    _onUpdateHiddenBackgroundState(newState) {
        this._updateHiddenBackgroundState(newState);
    }

    /**
     * Event handler generated by the model to toggle the dark theme
     * @param newState {Boolean}
     * @private
     */
    _onUpdateDarkTheme(newState) {
        this._updateDarkTheme(newState);
    }

    /**
     * Event handler generated by the loading mask dots
     * @param newState {Boolean}
     * @private
     */
    _animationEventHandler(/*event*/) {
        this._animationCompleteCheck();
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Allows performant JS animations to occur during non busy times of the JS event loop
     * @param time (Integer)
     * @private
     */
    _triggerTweenAnimations(time) {
        if (this._transitioning === true) {
            requestAnimationFrame(this._triggerTweenAnimations.bind(this));
            TWEEN.update(time);
        }
    }

    /**
     * Animates the veil into view.
     * @private
     */
    _show() {
        this._transitioning = true;
        this._triggerTweenAnimations();
        let transitionProperty = {opacity: 0};
        this._rootElem.classList.add("shown");
        new TWEEN.Tween(transitionProperty)
            .to({opacity: 1}, this._model._ANIMATION_DURATION)
            .easing(TWEEN.Easing.Linear.None)
            .onUpdate(() => {
                this._rootElem.style.opacity = transitionProperty.opacity;
            })
            .onComplete(() => {
                this._transitioning = false;
                this._startLoadingAnimation();
                this._model.emit("ShowComplete");
            })
            .start();
    }

    /**
     * Animates the veil out of view.
     * @private
     */
    _hide() {
        this._endLoadingAnimation();
        this._transitioning = true;
        this._triggerTweenAnimations();
        let transitionProperty = {opacity: 1};
        new TWEEN.Tween(transitionProperty)
            .to({opacity: 0}, this._model._ANIMATION_DURATION)
            .easing(TWEEN.Easing.Linear.None)
            .onUpdate(() => {
                this._rootElem.style.opacity = transitionProperty.opacity;
            })
            .onComplete(() => {
                this._rootElem.classList.remove("shown");
                this._transitioning = false;
                this._model.emit("HideComplete");
            })
            .start();
    }

    /**
     * Trigger the animated dots CSS animation
     * @private
     */
    _startLoadingAnimation() {
        this._loadingAnimationActive = true;
        this._loadingMaskContainer.classList.add("transitioning");
    }

    /**
     * Remove the animated dots CSS animation
     * @private
     */
    _endLoadingAnimation() {
        this._loadingAnimationActive = false;
        this._loadingMaskContainer.classList.remove("transitioning");
    }

    /**
     * Updates the visibility of the background
     * @param newState {Boolean}
     * @private
     */
    _updateHiddenBackgroundState(newState) {
        if (newState === true) {
            this._rootElem.classList.add("background-hidden");
        } else {
            this._rootElem.classList.remove("background-hidden");
        }
    }

    /**
     * Toggles the dark theme.
     * @param newState {Boolean}
     * @private
     */
    _updateDarkTheme(newState) {
        if (newState === true) {
            this._rootElem.classList.add("dark");
        } else {
            this._rootElem.classList.remove("dark");
        }
    }

    /**
     * Checks against the receipt of the hide event and (if received) hides the loading display.
     * @private
     */
    _animationCompleteCheck() {
        if (this._hideAcknowledged) {
            this._hideAcknowledged = false;
            this._hide();
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = FancyLoadingMaskGLRenderer;
